import { gql, useQuery } from '@apollo/client';
import { Card } from '@ovComponents/2-component/card/card';
import { UserContext } from '@providers/userContextProvider';
import { formatMoneyValue, formatPercentValue } from '@utils/moneyFormatting';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useContext } from 'react';

type IMarketData = {
  shortName: string;
  ticker: string;
  type: string;
  exchange: string;
  currency: string;
  statistics: IMarketDataStatistics;
};

type IMarketDataStatistics = {
  changeCents: number;
  changePercent: number;
  lastCents: number;
};

const FETCH_MARKET_DATA = gql`
  query FetchMarketDataFromProvider($input: FetchMarketDataFromProviderInput!) {
    fetchMarketDataFromProvider(input: $input) {
      marketData {
        shortName
        ticker
        type
        exchange
        currency
        statistics {
          changeCents
          changePercent
          lastCents
        }
      }
    }
  }
`;

export const MarketDataTicker = ({
  options = {
    tickers: '',
  },
}: {
  options: {
    tickers: string;
  };
}) => {
  const { tickers } = options;
  const { activeOrganization, integrations } = useContext(UserContext);
  const theme = useTheme();

  const hasIntegration = integrations.some(
    (integration) =>
      integration.type === 'MARKET_DATA' &&
      integration.provider === 'QUOTE_MEDIA' &&
      integration.organization.id === activeOrganization.id,
  );

  const { data, loading } = useQuery(FETCH_MARKET_DATA, {
    variables: {
      input: {
        organizationId: activeOrganization.id,
        tickers,
      },
    },
    skip: !hasIntegration,
  });

  const findMarketData = (ticker: string): IMarketData =>
    data?.fetchMarketDataFromProvider?.marketData?.find((marketData: any) =>
      marketData.ticker.includes(ticker),
    ) || {
      shortName: ticker,
      ticker,
      exchange: '',
      currency: '',
      statistics: {
        changeCents: 0,
        changePercent: 0,
        lastCents: 0,
      },
    };

  if (!hasIntegration) return null;

  return (
    <Card
      sx={{
        overflowX: 'auto',
        maxHeight: '76px',
        '&::-webkit-scrollbar': {
          height: '4px',
          display: 'none',
        },
        '&:hover::-webkit-scrollbar': {
          display: 'block',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: `${theme.color.onSurfaceVariant}33`,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: `${theme.color.onSurfaceVariant}4D`,
          borderRadius: '4px',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {tickers.split(',').map((ticker: any) => (
          <Box
            key={ticker}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              alignItems: 'start',
              width: '100%',
              justifyContent: 'space-between',
              minWidth: 140,

              '&:not(:last-of-type)': {
                borderRight: `1px solid ${theme.color.outlineVariant}`,
              },

              p: 2,
            }}
          >
            {loading ? (
              <Skeleton variant='text' width='70%' height='24px' />
            ) : (
              <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
                {findMarketData(ticker)?.shortName}
              </Typography>
            )}
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Skeleton variant='text' width='80px' height='20px' />
                <Skeleton variant='text' width='45px' height='20px' />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  color='textSecondary'
                  variant='bodyMedium'
                  sx={{ fontWeight: 500 }}
                >
                  {formatMoneyValue(
                    findMarketData(ticker).statistics.lastCents,
                    '$',
                    2,
                    true,
                    findMarketData(ticker).type !== 'INDEX',
                  )}{' '}
                  {findMarketData(ticker).type !== 'INDEX'
                    ? findMarketData(ticker).currency
                    : ''}
                </Typography>
                <Typography
                  variant='bodyMedium'
                  sx={{
                    color:
                      findMarketData(ticker).statistics.changePercent > 0
                        ? theme.color.positive
                        : theme.color.negative,
                    fontWeight: 500,
                  }}
                >
                  {findMarketData(ticker).statistics.changePercent > 0
                    ? '+'
                    : ''}
                  {formatPercentValue(
                    findMarketData(ticker).statistics.changePercent / 100,
                    2,
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Card>
  );
};
